import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  AttachMoney as DollarIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { getSubmissionTimestamp } from "../../utils/submissionUtils";

interface SubmissionData {
  _id: string;
  uniqueId: string;
  editCode: string;
  form: {
    _id: string;
    title: string;
    fields: any[];
  };
  data: Record<string, any>;
  status: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  hasPayment: boolean;
  editHistory?: Array<{
    editedAt: string;
    editedBy: string;
    previousData: Record<string, any>;
    changedFields: string[];
    newData?: Record<string, any>; // Add this to track the values after the edit
  }>;
  paymentInfo?: {
    amount: number;
    currency: string;
    method: string;
    status: string;
    receiptPath?: string;
    originalPaymentId?: string;
    bkashPaymentId?: string;
  };
}

const AdminSubmissionView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState<string>("");
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>("");
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Communication dialog state
  const [communicationDialog, setCommunicationDialog] = useState<{
    open: boolean;
    type: 'email' | 'sms';
    subject: string;
    message: string;
    selectedField: string;
    availableVariables: Array<{ key: string; value: string; category: string; displayLabel?: string }>;
  }>({
    open: false,
    type: 'email',
    subject: '',
    message: '',
    selectedField: '',
    availableVariables: [],
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const loadSubmission = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSubmission(response.data);
      } catch (error: any) {
        console.error("Failed to load submission:", error);
        setError(
          error.response?.data?.message || "Failed to load submission details"
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadSubmission();
    }
  }, [id, refreshKey]);

  // Separate useEffect for handling navigation state to prevent infinite loops
  useEffect(() => {
    // Force refresh if coming back from edit page
    if (location.state?.refreshData) {
      setRefreshKey((prev) => prev + 1);
      // Clear the state to prevent infinite refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.refreshData, location.pathname, navigate]);

  const handleRefreshData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSubmission(response.data);
    } catch (error: any) {
      console.error("Failed to refresh data:", error);
      setError("Failed to refresh submission data");
    } finally {
      setLoading(false);
    }
  };

  const handleViewReceipt = (receiptPath: string) => {
    const normalizedPath = receiptPath.replace(/\\/g, "/");
    const fullUrl = normalizedPath.startsWith("http")
      ? normalizedPath
      : `${process.env.REACT_APP_API_URL}${normalizedPath.startsWith("/") ? "" : "/"}${normalizedPath}`;

    setPdfPreviewError(null);
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
      setPdfPreviewUrl("");
    }

    setReceiptUrl(fullUrl);
    setReceiptModalOpen(true);
  };

  useEffect(() => {
    const loadPdfPreview = async () => {
      const fileType = getFileType(receiptUrl);
      if (!receiptModalOpen || !receiptUrl || fileType !== "pdf") {
        return;
      }

      try {
        setPdfPreviewLoading(true);
        setPdfPreviewError(null);

        const response = await axios.get(receiptUrl, {
          responseType: "blob",
        });

        const blobUrl = URL.createObjectURL(response.data);
        setPdfPreviewUrl(blobUrl);
      } catch (err) {
        console.error("Failed to load PDF preview:", err);
        setPdfPreviewError("Unable to preview PDF in modal. Please use Download.");
      } finally {
        setPdfPreviewLoading(false);
      }
    };

    loadPdfPreview();
  }, [receiptModalOpen, receiptUrl]);

  useEffect(() => {
    return () => {
      if (pdfPreviewUrl) {
        URL.revokeObjectURL(pdfPreviewUrl);
      }
    };
  }, [pdfPreviewUrl]);

  const handleDownloadReceipt = (receiptPath: string) => {
    const normalizedPath = receiptPath.replace(/\\/g, "/");
    const downloadUrl = normalizedPath.startsWith("http")
      ? normalizedPath
      : `${process.env.REACT_APP_API_URL}${normalizedPath.startsWith("/") ? "" : "/"}${normalizedPath}`;
    window.open(downloadUrl, "_blank");
  };

  const handleDownloadPDF = async () => {
    try {
      const token = localStorage.getItem("token");

      // First, let's test if the endpoint is reachable with a simple request
      try {
        await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } catch (testError: any) {
        console.error("📄 Frontend: Test endpoint failed:", testError);
        throw new Error(
          "Cannot access submission data. Please check if the submission exists and you have permission."
        );
      }

      // Now try the PDF download
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/${id}/export/pdf`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
          responseType: "blob", // Important for file downloads
          timeout: 30000, // 30 second timeout
        }
      );

      // Get the blob from response
      const blob = response.data;

      if (blob.size === 0) {
        throw new Error("PDF file is empty");
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `submission_${submission?.uniqueId}_${
        new Date().toISOString().split("T")[0]
      }_${new Date().toISOString().split("T")[1]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error("📄 Frontend: PDF download error:", error);
      console.error("📄 Frontend: Error response:", error.response?.data);
      console.error("📄 Frontend: Error status:", error.response?.status);
      console.error("📄 Frontend: Error config:", error.config);

      // Show user-friendly error message
      let errorMessage = "Unknown error";
      if (
        error.code === "NETWORK_ERROR" ||
        error.message.includes("Network Error")
      ) {
        errorMessage =
          "Network error - please check your connection and try again";
      } else if (error.response?.status === 404) {
        errorMessage = "PDF export endpoint not found";
      } else if (error.response?.status === 401) {
        errorMessage = "Authentication failed - please login again";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied - admin permissions required";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      alert(`Failed to download PDF: ${errorMessage}`);
    }
  };

  const renderFieldValue = (field: any, value: any) => {
    if (!value && value !== 0) return "N/A";

    switch (field.type) {
      case "checkbox":
        return Array.isArray(value) ? value.join(", ") : value;
      case "file":
        if (Array.isArray(value)) {
          return value.map((filePath, index) => (
            <Box key={index} display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                {filePath.split("/").pop()}
              </Typography>
              <IconButton
                size="small"
                title="View"
                onClick={() => handleViewReceipt(filePath)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                title="Download"
                onClick={() => handleDownloadReceipt(filePath)}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Box>
          ));
        }
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">{value.split("/").pop()}</Typography>
            <IconButton
              size="small"
              title="View"
              onClick={() => handleViewReceipt(value)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Download"
              onClick={() => handleDownloadReceipt(value)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      default:
        return typeof value === "object"
          ? JSON.stringify(value)
          : String(value);
    }
  };

  const getEffectiveFieldValue = (field: any) => {
    if (!submission) return null;

    // Always use the current submission data, which should reflect all edits
    // Both admin and user edits update submission.data directly
    return getFieldValue(field);
  };

  const getFieldValue = (field: any) => {
    if (!submission) return null;

    // Try multiple possible keys to find the field value
    // Check all possible field identifiers to get the current value
    const possibleKeys = [
      field.id, // Prioritize field.id (our standard key)
      field.name,
      field._id,
      `field_${field.id}`,
      `field_${field._id}`,
      `field_${field.name}`,
    ];

    for (const key of possibleKeys) {
      if (key && submission.data.hasOwnProperty(key)) {
        return submission.data[key];
      }
    }

    return null;
  };

  const findFieldByAnyId = (fieldId: string) => {
    return submission?.form.fields.find(
      (f) =>
        f.id === fieldId ||
        f._id === fieldId ||
        f.name === fieldId ||
        `field_${f.id}` === fieldId ||
        `field_${f._id}` === fieldId
    );
  };

  const formatValueForDisplay = (value: any): string => {
    if (value === null || value === undefined) return "Empty";
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "Empty";
    }
    if (typeof value === "object") {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Communication handlers
  const generateAvailableVariables = (): Array<{ key: string; value: string; category: string; displayLabel?: string }> => {
    if (!submission) return [];

    const variables: Array<{ key: string; value: string; category: string; displayLabel?: string }> = [];

    // System Variables (matching FormBuilder pattern exactly)
    variables.push(
      { key: '{{formTitle}}', value: submission.form.title, category: 'System' },
      { key: '{{submissionId}}', value: submission._id, category: 'System' },
      { key: '{{uniqueId}}', value: submission.uniqueId, category: 'System' },
      { key: '{{intUniqueId}}', value: getSubmissionTimestamp(submission.uniqueId) ?? '', category: 'System' },
      { key: '{{submissionTimestamp}}', value: new Date(submission.createdAt).toLocaleString(), category: 'System' },
    );

    // Generate allFields content for {{allFields}} variable
    const allFieldsContent = submission.form.fields
      .filter(field => !['html', 'heading', 'paragraph', 'divider'].includes(field.type))
      .map(field => {
        const value = getFieldValue(field);
        const displayValue = formatValueForDisplay(value);
        return `${field.label}: ${displayValue}`;
      })
      .join('\n');
    
    variables.push({ key: '{{allFields}}', value: allFieldsContent, category: 'System' });

    // Payment Variables - ALWAYS show for all submissions
    // Basic payment variables that are always available
    variables.push(
      { key: '{{editCode}}', value: submission.editCode || 'N/A', category: 'Payment' }
    );

    // Add retry payment link - always available
    const retryLink = `${window.location.origin}/retry-payment/${submission.uniqueId}/${submission.paymentInfo?.originalPaymentId || submission.paymentInfo?.bkashPaymentId || 'stripe'}`;
    variables.push({ key: '{{retryPaymentLink}}', value: retryLink, category: 'Payment' });

    // Add payment-specific variables if payment info exists
    if (submission.hasPayment || submission.paymentInfo) {
      variables.push(
        { key: '{{paymentStatus}}', value: submission.status, category: 'Payment' },
        { key: '{{paymentAmount}}', value: submission.paymentInfo?.amount ? `৳${submission.paymentInfo.amount}` : 'N/A', category: 'Payment' },
        { key: '{{paymentMethod}}', value: submission.paymentInfo?.method || 'N/A', category: 'Payment' }
      );
    } else {
      variables.push(
        { key: '{{paymentStatus}}', value: submission.status || 'pending', category: 'Payment' },
        { key: '{{paymentAmount}}', value: 'N/A', category: 'Payment' },
        { key: '{{paymentMethod}}', value: 'N/A', category: 'Payment' }
      );
    }

    // Form Field Variables (using field name as variable key, matching FormBuilder pattern)
    submission.form.fields.forEach(field => {
      if (!['html', 'heading', 'paragraph', 'divider'].includes(field.type)) {
        const value = getFieldValue(field);
        const displayValue = formatValueForDisplay(value);
        const variableKey = `{{${field.name || field.id}}}`;
        const displayLabel = `${field.label} (${field.name || field.id})`;
        variables.push({ 
          key: variableKey, 
          value: displayValue, 
          category: 'Form Fields',
          displayLabel: displayLabel // Add display label for better UX
        });
      }
    });

    return variables;
  };

  const handleOpenCommunication = (type: 'email' | 'sms') => {
    const variables = generateAvailableVariables();
    
    // Auto-select first available field for the communication type
    const availableFields = submission?.form.fields.filter(field => 
      type === 'email' ? field.type === 'email' : (field.type === 'tel' || field.type === 'phone')
    ) || [];
    
    setCommunicationDialog({
      open: true,
      type,
      subject: type === 'email' ? `Regarding your submission: {{formTitle}}` : '',
      message: type === 'email' 
        ? `Dear Applicant,

This is regarding your submission (ID: {{uniqueId}}) for {{formTitle}}.

Thank you for your submission. We have received your application and will process it accordingly.

Best regards,
Admin Team`
        : `Hi! Regarding your submission (ID: {{uniqueId}}) for {{formTitle}}. Thank you for your application.`,
      selectedField: availableFields.length > 0 ? availableFields[0].id : '',
      availableVariables: variables,
    });
  };

  const handleSendCommunication = async () => {
    if (!submission) return;

    try {
      setSending(true);
      const token = localStorage.getItem("token");

      // Get the selected field and its value
      const selectedField = submission.form.fields.find(field => field.id === communicationDialog.selectedField);
      if (!selectedField) {
        throw new Error('Selected field not found');
      }

      // Get the actual field value (phone number or email)
      const fieldValue = getFieldValue(selectedField);
      if (!fieldValue) {
        throw new Error(`No ${communicationDialog.type === 'email' ? 'email address' : 'phone number'} found for selected field`);
      }

      console.log('📤 Sending communication:', {
        type: communicationDialog.type,
        selectedField: selectedField.id,
        fieldValue: fieldValue,
        fieldLabel: selectedField.label
      });

      // Replace variables in the message
      let processedMessage = communicationDialog.message;
      let processedSubject = communicationDialog.subject;

      communicationDialog.availableVariables.forEach(variable => {
        const regex = new RegExp(variable.key.replace(/[{}]/g, '\\$&'), 'g');
        processedMessage = processedMessage.replace(regex, variable.value);
        if (communicationDialog.type === 'email') {
          processedSubject = processedSubject.replace(regex, variable.value);
        }
      });

      const payload = {
        submissionId: submission.uniqueId,
        type: communicationDialog.type,
        message: processedMessage,
        selectedField: communicationDialog.selectedField, // Keep field ID for backend validation
        fieldValue: fieldValue, // Send the actual phone/email value
        ...(communicationDialog.type === 'email' && { subject: processedSubject }),
      };

      console.log('📤 Payload being sent:', payload);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/submissions/${submission.uniqueId}/communicate`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Close dialog and show success
      setCommunicationDialog(prev => ({ ...prev, open: false }));
      alert(`${communicationDialog.type === 'email' ? 'Email' : 'SMS'} sent successfully to ${fieldValue}`);
      
    } catch (error: any) {
      console.error('Failed to send communication:', error);
      setError(`Failed to send ${communicationDialog.type}: ${error.response?.data?.message || error.message}`);
    } finally {
      setSending(false);
    }
  };

  const insertVariable = (variableKey: string) => {
    setCommunicationDialog(prev => ({
      ...prev,
      message: prev.message + variableKey,
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const getFileType = (url: string): "image" | "pdf" | "unknown" => {
    const extension = url.split(".").pop()?.toLowerCase();
    if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension || "")
    ) {
      return "image";
    } else if (extension === "pdf") {
      return "pdf";
    }
    return "unknown";
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ mt: 4, display: "flex", justifyContent: "center" }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/submissions")}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Container>
    );
  }

  if (!submission) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning">Submission not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin/submissions")}
          sx={{ mt: 2 }}
        >
          Back
        </Button>
      </Container>
    );
  }

  const fileType = receiptUrl ? getFileType(receiptUrl) : "unknown";

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        mt: { xs: 2, sm: 4 }, 
        mb: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 }
      }}
    >
      {/* Header */}
      <Box mb={3}>
        {/* Title and Back Button */}
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/admin/submissions")}
            variant="outlined"
            size="small"
            sx={{ 
              fontSize: { xs: "0.75rem", sm: "0.875rem" }
            }}
          >
            Back
          </Button>
          <Typography 
            component="h1"
            sx={{ 
              fontSize: { xs: "1.5rem", sm: "2.125rem" },
              fontWeight: 400,
              lineHeight: 1.235,
              letterSpacing: "0.00735em"
            }}
          >
            Submission Details
          </Typography>
        </Box>

        {/* Action Buttons - Responsive Layout */}
        <Box 
          display="flex" 
          flexDirection={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }} 
          justifyContent={{ xs: "flex-start", sm: "space-between" }}
          gap={2}
        >
          {/* Primary Actions */}
          <Box 
            display="flex" 
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1}
            flex={1}
          >
            <Button
              startIcon={<RefreshIcon />}
              onClick={handleRefreshData}
              variant="outlined"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Refresh
            </Button>
            <Button
              startIcon={<EmailIcon />}
              onClick={() => handleOpenCommunication('email')}
              variant="outlined"
              color="info"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Send Email
            </Button>
            <Button
              startIcon={<SmsIcon />}
              onClick={() => handleOpenCommunication('sms')}
              variant="outlined"
              color="info"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Send SMS
            </Button>
          </Box>

          {/* Secondary Actions */}
          <Box 
            display="flex" 
            flexDirection={{ xs: "column", sm: "row" }}
            gap={1}
            alignItems="center"
          >
            <Button
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/submissions/${id}/edit`)}
              variant="outlined"
              color="primary"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Edit
            </Button>
            <Button
              startIcon={<PdfIcon />}
              onClick={handleDownloadPDF}
              variant="contained"
              color="primary"
              size="small"
              sx={{ 
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Download PDF
            </Button>
            <Chip
              label={submission.status.toUpperCase()}
              color={getStatusColor(submission.status) as any}
              variant="filled"
              sx={{ alignSelf: { xs: "center", sm: "auto" } }}
            />
          </Box>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection={{ xs: "column", lg: "row" }}
        gap={3}
        mb={3}
      >
        {/* Basic Information */}
        <Box flex={1}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {/* Responsive Grid Layout */}
              <Box 
                display="grid" 
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={2}
                mb={2}
              >
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Submission ID
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: "break-all",
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }}
                  >
                    {submission.uniqueId}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Form Title
                  </Typography>
                  <Typography variant="body1">{submission.form.title}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Submitted At
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    {new Date(submission.createdAt).toLocaleString()}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Edit Code
                  </Typography>
                  <Typography
                    variant="body1"
                    fontFamily="monospace"
                    color="primary.main"
                    sx={{ 
                      wordBreak: "break-all",
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }}
                  >
                    {submission.editCode}
                  </Typography>
                </Box>

                <Box sx={{ gridColumn: { sm: "1 / -1" } }}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Last Updated
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    {new Date(submission.updatedAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* Edit History */}
              {submission.editHistory && submission.editHistory.length > 0 && (
                <Box mb={2}>
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                  >
                    Edit History ({submission.editHistory.length} edit
                    {submission.editHistory.length > 1 ? "s" : ""})
                  </Typography>
                  {submission.editHistory.map((edit, index) => {
                    // Get field labels for changed fields only
                    const changedFieldLabels = edit.changedFields
                      .map((fieldId) => {
                        const field = findFieldByAnyId(fieldId);
                        return field
                          ? field.label || field.name || fieldId
                          : fieldId;
                      })
                      .filter((label) => label); // Remove any empty labels

                    return (
                      <Box
                        key={index}
                        sx={{
                          mb: 2,
                          p: { xs: 1, sm: 2 },
                          bgcolor: "grey.50",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          gutterBottom
                          sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                        >
                          Edit #{index + 1}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          gutterBottom
                          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                        >
                          Edited on {new Date(edit.editedAt).toLocaleString()}{" "}
                          by {edit.editedBy}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ 
                            mt: 1, 
                            fontWeight: "medium",
                            fontSize: { xs: "0.875rem", sm: "1rem" }
                          }}
                        >
                          Changed Fields: {changedFieldLabels.join(", ")}
                        </Typography>

                        {/* Show previous vs current data for changed fields */}
                        {edit.previousData && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              gutterBottom
                            >
                              Changes Made:
                            </Typography>
                            {edit.changedFields
                              .map((fieldId) => {
                                const field = findFieldByAnyId(fieldId);
                                const fieldLabel = field
                                  ? field.label || field.name || fieldId
                                  : fieldId;

                                // Get the "before" value - search through possible keys in previousData
                                let previousValue = null;
                                if (field && edit.previousData) {
                                  const possibleKeys = [
                                    field.id,
                                    field.name,
                                    field._id,
                                    `field_${field.id}`,
                                    `field_${field._id}`,
                                    `field_${field.name}`,
                                    fieldId, // Also try the original fieldId from changedFields
                                  ];

                                  for (const key of possibleKeys) {
                                    if (
                                      key &&
                                      edit.previousData.hasOwnProperty(key)
                                    ) {
                                      previousValue = edit.previousData[key];
                                      break;
                                    }
                                  }
                                }

                                // Get the "after" value - search through possible keys in newData
                                let afterValue = null;
                                if (field && edit.newData) {
                                  const possibleKeys = [
                                    field.id,
                                    field.name,
                                    field._id,
                                    `field_${field.id}`,
                                    `field_${field._id}`,
                                    `field_${field.name}`,
                                    fieldId, // Also try the original fieldId from changedFields
                                  ];

                                  for (const key of possibleKeys) {
                                    if (
                                      key &&
                                      edit.newData.hasOwnProperty(key)
                                    ) {
                                      afterValue = edit.newData[key];
                                      break;
                                    }
                                  }
                                } else {
                                  // If newData is not available for some reason, skip this change
                                  return null;
                                }

                                // Only show if there's actually a difference
                                if (
                                  formatValueForDisplay(previousValue) ===
                                  formatValueForDisplay(afterValue)
                                ) {
                                  return null;
                                }

                                return {
                                  fieldId,
                                  fieldLabel,
                                  previousValue,
                                  afterValue,
                                };
                              })
                              .filter((change) => change !== null) // Remove null entries
                              .slice(0, 5) // Limit to first 5 changes
                              .map((change) => {
                                if (!change) return null;

                                return (
                                  <Box
                                    key={change.fieldId}
                                    sx={{
                                      ml: { xs: 0, sm: 1 },
                                      mt: 1,
                                      p: 1,
                                      bgcolor: "background.paper",
                                      border: "1px solid",
                                      borderColor: "grey.300",
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      sx={{ 
                                        fontWeight: "bold", 
                                        mb: 0.5,
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" }
                                      }}
                                    >
                                      {change.fieldLabel}:
                                    </Typography>
                                    <Box sx={{ 
                                      display: "flex", 
                                      flexDirection: { xs: "column", sm: "row" },
                                      gap: { xs: 1, sm: 2 }
                                    }}>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="caption"
                                          component="div"
                                          sx={{
                                            color: "#d32f2f",
                                            backgroundColor: "#ffebee",
                                            p: 0.5,
                                            borderRadius: 0.5,
                                            fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                          }}
                                        >
                                          <strong>Before:</strong>{" "}
                                          {formatValueForDisplay(
                                            change.previousValue
                                          )}
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1 }}>
                                        <Typography
                                          variant="caption"
                                          component="div"
                                          sx={{
                                            color: "#2e7d32",
                                            backgroundColor: "#e8f5e8",
                                            p: 0.5,
                                            borderRadius: 0.5,
                                            fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                          }}
                                        >
                                          <strong>After:</strong>{" "}
                                          {formatValueForDisplay(
                                            change.afterValue
                                          )}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })}
                            {(() => {
                              const actualChanges = edit.changedFields.filter(
                                (fieldId) => {
                                  const previousValue =
                                    edit.previousData[fieldId];
                                  let afterValue = null;
                                  if (
                                    edit.newData &&
                                    edit.newData.hasOwnProperty(fieldId)
                                  ) {
                                    afterValue = edit.newData[fieldId];
                                  }
                                  return (
                                    afterValue !== null &&
                                    formatValueForDisplay(previousValue) !==
                                      formatValueForDisplay(afterValue)
                                  );
                                }
                              );

                              return (
                                actualChanges.length > 5 && (
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    sx={{ ml: 1, fontStyle: "italic" }}
                                  >
                                    ... and {actualChanges.length - 5} more
                                    fields
                                  </Typography>
                                )
                              );
                            })()}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Payment Information */}
        {submission.hasPayment && submission.paymentInfo && (
          <Box flex={1}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Responsive Grid Layout */}
                <Box 
                  display="grid" 
                  gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                  gap={2}
                  mb={2}
                >
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Amount
                    </Typography>
                    <Typography variant="body1">
                      {submission.paymentInfo.amount}{" "}
                      {submission.paymentInfo.currency?.toUpperCase()}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Payment Method
                    </Typography>
                    <Typography variant="body1">
                      {submission.paymentInfo.method}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Payment Status
                    </Typography>
                    <Chip
                      label={submission.paymentInfo.status.toUpperCase()}
                      color={getStatusColor(submission.paymentInfo.status) as any}
                      size="small"
                    />
                  </Box>

                  {submission.paymentInfo.receiptPath && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Receipt
                      </Typography>
                      <Box 
                        display="flex" 
                        flexDirection={{ xs: "column", sm: "row" }}
                        gap={1}
                      >
                        <Button
                          size="small"
                          startIcon={<DollarIcon />}
                          onClick={() =>
                            handleViewReceipt(
                              submission.paymentInfo!.receiptPath!
                            )
                          }
                          variant="outlined"
                          sx={{ 
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          View Receipt
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() =>
                            handleDownloadReceipt(
                              submission.paymentInfo!.receiptPath!
                            )
                          }
                          variant="outlined"
                          sx={{ 
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          Download
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>

      {/* Admin Notes */}
      {submission.adminNotes && (
        <Box mb={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Admin Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1" style={{ whiteSpace: "pre-wrap" }}>
                {submission.adminNotes}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Form Data */}
      <Box>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Form Data
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Responsive Grid Layout for Form Fields */}
            <Box 
              display="grid" 
              gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
              gap={3}
            >
              {submission.form.fields
                .filter((field) => field.type !== 'html') // Hide HTML content fields from submission view
                .map((field) => (
                <Box 
                  key={field._id} 
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 1,
                    bgcolor: "grey.50"
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    color="textSecondary"
                    gutterBottom
                    sx={{ 
                      fontWeight: "bold",
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }}
                  >
                    {field.label}
                    {field.required && <span style={{ color: "red" }}> *</span>}
                  </Typography>
                  <Box 
                    sx={{ 
                      wordBreak: "break-word",
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }}
                  >
                    {renderFieldValue(field, getEffectiveFieldValue(field))}
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Receipt Modal */}
      <Dialog
        open={receiptModalOpen}
        onClose={() => {
          setReceiptModalOpen(false);
          setPdfPreviewError(null);
          if (pdfPreviewUrl) {
            URL.revokeObjectURL(pdfPreviewUrl);
            setPdfPreviewUrl("");
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "600px" },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <SettingsIcon color="action" />
              <Typography variant="h6">Receipt</Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => window.open(receiptUrl, "_blank")}
              size="small"
            >
              Download
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {fileType === "image" ? (
            <Box
              component="img"
              src={receiptUrl}
              alt="Receipt"
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                objectFit: "contain",
              }}
            />
          ) : fileType === "pdf" ? (
            <Box sx={{ height: "70vh", width: "100%" }}>
              {pdfPreviewLoading ? (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : pdfPreviewError ? (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Alert severity="warning">{pdfPreviewError}</Alert>
                </Box>
              ) : (
                <iframe
                  src={pdfPreviewUrl || receiptUrl}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                  title="Receipt PDF"
                />
              )}
            </Box>
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body1" color="textSecondary">
                File type not supported for preview. Please download to view.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReceiptModalOpen(false);
              setPdfPreviewError(null);
              if (pdfPreviewUrl) {
                URL.revokeObjectURL(pdfPreviewUrl);
                setPdfPreviewUrl("");
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Communication Dialog */}
      <Dialog
        open={communicationDialog.open}
        onClose={() => setCommunicationDialog(prev => ({ ...prev, open: false }))}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: '90vh' },
            height: { xs: '100vh', sm: 'auto' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography 
            variant="h5"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Send {communicationDialog.type === 'email' ? 'Email' : 'SMS'} Communication
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Send a custom {communicationDialog.type} to the submission user
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ mt: 2 }}>
            {/* Subject field for email */}
            {communicationDialog.type === 'email' && (
              <TextField
                fullWidth
                label="Subject"
                value={communicationDialog.subject}
                onChange={(e) => setCommunicationDialog(prev => ({ 
                  ...prev, 
                  subject: e.target.value 
                }))}
                sx={{ 
                  mb: 2,
                  '& .MuiInputBase-root': {
                    height: { xs: '40px', sm: '56px' }
                  }
                }}
              />
            )}

            {/* Field Selection */}
            <FormControl 
              fullWidth 
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root': {
                  height: { xs: '40px', sm: '56px' }
                }
              }}
            >
              <InputLabel>
                {communicationDialog.type === 'email' ? 'Email Field' : 'Phone Field'}
              </InputLabel>
              <Select
                value={communicationDialog.selectedField}
                onChange={(e) => setCommunicationDialog(prev => ({ 
                  ...prev, 
                  selectedField: e.target.value 
                }))}
                label={communicationDialog.type === 'email' ? 'Email Field' : 'Phone Field'}
              >
                {submission?.form.fields
                  .filter(field => 
                    communicationDialog.type === 'email' 
                      ? field.type === 'email' 
                      : (field.type === 'tel' || field.type === 'phone')
                  )
                  .map(field => (
                    <MenuItem key={field.id} value={field.id}>
                      <Box>
                        <Typography variant="body2">{field.label}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          ({field.name || field.id})
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
              </Select>
              {submission?.form.fields.filter(field => 
                communicationDialog.type === 'email' 
                  ? field.type === 'email' 
                  : (field.type === 'tel' || field.type === 'phone')
              ).length === 0 && (
                <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                  No {communicationDialog.type === 'email' ? 'email' : 'phone'} fields found in this form
                </Typography>
              )}
            </FormControl>

            {/* Message field */}
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={8}
              label="Message"
              value={communicationDialog.message}
              onChange={(e) => setCommunicationDialog(prev => ({ 
                ...prev, 
                message: e.target.value 
              }))}
              placeholder={`Enter your ${communicationDialog.type} message here...`}
              sx={{ 
                mb: 2,
                '& .MuiInputBase-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }
              }}
            />

            {/* Available Variables */}
            <Accordion>
              <AccordionSummary>
                <Typography 
                  variant="h6"
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Available Variables (click to insert)
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 1, sm: 2 } }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Click on any variable below to insert it into your message. Variables will be replaced with actual values when sent.
                </Typography>
                
                {/* Group variables by category */}
                {['System', 'Payment', 'Form Fields'].map(category => {
                  const categoryVars = communicationDialog.availableVariables.filter(v => v.category === category);
                  if (categoryVars.length === 0) return null;
                  
                  return (
                    <Box key={category} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {category} Variables:
                      </Typography>
                      <Paper sx={{ p: 1, bgcolor: 'grey.50' }}>
                        <Box sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: { xs: 0.5, sm: 1 }
                        }}>
                          {categoryVars.map(variable => (
                            <Chip
                              key={variable.key}
                              label={variable.displayLabel || variable.key}
                              variant="outlined"
                              size="small"
                              clickable
                              onClick={() => insertVariable(variable.key)}
                              title={`Variable: ${variable.key}\nCurrent value: ${variable.value}`}
                              color={category === 'Form Fields' ? 'secondary' : 'default'}
                              sx={{ 
                                fontSize: { xs: "0.7rem", sm: "0.8125rem" },
                                maxWidth: { xs: "45%", sm: "none" }
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Box>
                  );
                })}
              </AccordionDetails>
            </Accordion>

            {/* Examples Section - matching FormBuilder pattern */}
            <Box sx={{ mt: 2, p: { xs: 1, sm: 2 }, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Variable Usage Examples:
              </Typography>
              
              <Box sx={{ mt: 2, p: { xs: 1, sm: 1.5 }, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="caption" fontWeight="bold">
                  Examples:
                </Typography>
                <Typography 
                  variant="caption" 
                  display="block" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    mt: 0.5,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    wordBreak: "break-word"
                  }}
                >
                  "Hi, your application for {'{{formTitle}}'} has been received! Reference: {'{{uniqueId}}'}"
                </Typography>
                <Typography 
                  variant="caption" 
                  display="block" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    mt: 0.5,
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                    wordBreak: "break-word"
                  }}
                >
                  "Form submitted: {'{{formTitle}}'}{'\n'}{'{{allFields}}'}"
                </Typography>
                {(() => {
                  const formFieldVars = communicationDialog.availableVariables.filter(v => v.category === 'Form Fields');
                  const hasRetryLink = communicationDialog.availableVariables.some(v => v.key === '{{retryPaymentLink}}');
                  
                  if (hasRetryLink) {
                    return (
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          mt: 0.5,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          wordBreak: "break-word"
                        }}
                      >
                        "Payment required: {'{{retryPaymentLink}}'}"
                      </Typography>
                    );
                  }
                  
                  if (formFieldVars.length > 0) {
                    const firstField = formFieldVars[0];
                    return (
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          mt: 0.5,
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          wordBreak: "break-word"
                        }}
                      >
                        "Hello {firstField.key}, thank you for submitting {'{{formTitle}}'}"
                      </Typography>
                    );
                  }
                  return null;
                })()}
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1, 
                    display: 'block',
                    fontSize: { xs: "0.7rem", sm: "0.75rem" }
                  }}
                >
                  Use {'{{allFields}}'} to include all form data automatically.
                  {communicationDialog.availableVariables.some(v => v.key === '{{retryPaymentLink}}') && 
                    ' Use {{retryPaymentLink}} for payment retry links.'
                  }
                </Typography>
              </Box>
              
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  mt: 1, 
                  display: 'block',
                  fontSize: { xs: "0.7rem", sm: "0.75rem" }
                }}
              >
                Click any variable above to add it to your message. Variables will be replaced with actual submission values when sent.
              </Typography>
            </Box>

            {/* Preview */}
            <Box sx={{ mt: 2 }}>
              <Typography 
                variant="h6"
                gutterBottom
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' }
                }}
              >
                Preview (with variables replaced):
              </Typography>
              <Paper sx={{ 
                p: { xs: 1, sm: 2 }, 
                bgcolor: 'grey.50', 
                maxHeight: { xs: 150, sm: 200 }, 
                overflow: 'auto' 
              }}>
                {communicationDialog.type === 'email' && (
                  <Typography 
                    variant="body2" 
                    gutterBottom
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    <strong>Subject:</strong> {(() => {
                      let preview = communicationDialog.subject;
                      communicationDialog.availableVariables.forEach(variable => {
                        preview = preview.replace(new RegExp(variable.key.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
                      });
                      return preview;
                    })()}
                  </Typography>
                )}
                <Typography 
                  variant="body2" 
                  component="pre" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: { xs: "0.875rem", sm: "1rem" },
                    wordBreak: "break-word"
                  }}
                >
                  {(() => {
                    let preview = communicationDialog.message;
                    communicationDialog.availableVariables.forEach(variable => {
                      preview = preview.replace(new RegExp(variable.key.replace(/[{}]/g, '\\$&'), 'g'), variable.value);
                    });
                    return preview;
                  })()}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
          p: { xs: 2, sm: 1 }
        }}>
          <Button 
            onClick={() => setCommunicationDialog(prev => ({ ...prev, open: false }))}
            sx={{ 
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendCommunication}
            disabled={sending || !communicationDialog.message.trim() || !communicationDialog.selectedField}
            sx={{ 
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            {sending ? 'Sending...' : `Send ${communicationDialog.type === 'email' ? 'Email' : 'SMS'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSubmissionView;
